import {type SagaIterator} from 'redux-saga';
import {matchPath} from 'react-router-dom';
import {sendEvent} from '@optimaxdev/analytics/mobile';

import {staticUrls, dynamicRoutes, type StaticRouteType} from 'constants/router/router';
import {PRODUCT, QUOTE_ITEM} from 'constants/actions';

import {getLocation, getCurrentPage} from 'selectors/app/app';
import {call, fork, put, select, take} from 'redux-saga/effects';
import {saveAppPage} from 'actions/app/app';
import {getActiveProduct, getProductSku} from 'selectors/product/product';
import {getQuoteItems} from 'selectors/cart/cart';
import {visibilityMenu} from 'actions/menu/mainMenu';
import {setRouterInfo} from 'actions/routerInfo/routerInfo';
import {initialState as initialRouterState} from 'reducers/routerInfo/routerInfo';
import {actionsForChangePage} from './../actionsForChangePage/actionsForChangePage';

type DynamicPageType = {page: string, event: ?string, params: any, id: ?number};

/**
 * Returns product page suitable context
 * @returns {Array<string>} context
 */
export function* getProductPageSuitableContext(): SagaIterator<Array<string>> {
    const activeProduct = yield select(getActiveProduct);

    if (!activeProduct) {
        yield take(PRODUCT.SET_ACTIVE_PRODUCT_ID);
    }

    const productSku = yield select(getProductSku);

    return [productSku];
}

/**
 * Saga which returns suitable context data for received page
 * @param {DynamicPageType} page - Received page name
 * @returns {SagaIterator<number | string>} - Returned context data
 */
export function* returnSuitableContextData(page: DynamicPageType): SagaIterator<number | string> {
    if (page.page === 'category') {
        return [String(page.id)];
    } else if (page.page === 'product' || page.page === 'productEdit') {
        return yield call(getProductPageSuitableContext);
    } else if (page.page === 'cart') {
        yield take(`${QUOTE_ITEM.GET_ITEMS}_FULFILLED`);
        const cartItems = yield select(getQuoteItems);
        return cartItems.map(item => item.product_sku);
    }
    return '';
}

/**
 * Save needed context to DY
 * @param {DynamicPageType} page - current page data
 */
export function* sendContextDY(page: DynamicPageType): SagaIterator {
    const dyData = {context: {type: page.params.dyType}};

    const contextData = yield call(returnSuitableContextData, page);

    if (contextData) dyData.context.data = contextData;

    if (window.DY) {
        yield call(sendEvent, 'spa', dyData);
    }
}

/**
 * Save page to store
 * Then call saga for change page
 * @param {DynamicPageType} page - current page
 */
export function* savePage(page: DynamicPageType): SagaIterator {
    // save page to store
    const saveAppPageAction = yield call(saveAppPage, {...page, params: {...page.params}});
    yield put(saveAppPageAction);
    yield call(actionsForChangePage);

    const {
        location: {search},
    } = yield select(getLocation);

    if (!search.includes('NoDY')) {
        yield call(sendContextDY, page);
    }
}

/**
 * Get current page from url
 * Then save to store
 * @param {CustomEvent} event - custom dispatched event
 */
export function* saveLocation(event: CustomEvent): SagaIterator {
    yield put(visibilityMenu(false));
    const currentPage = yield select(getCurrentPage);
    const isCmsPage = currentPage.page === 'page';

    const {
        location: {pathname, state: locationState},
    } = yield select(getLocation);
    const page = staticUrls.find((route: StaticRouteType): boolean => {
        const matches = matchPath(pathname, route.url);
        return matches && matches.isExact;
    });

    if (
        typeof locationState === 'undefined' &&
        !isCmsPage &&
        !['WIZARD_START', 'WIZARD_END'].includes(event?.detail?.data)
    )
        yield put(setRouterInfo(initialRouterState));

    if (page) {
        yield call(savePage, page);
    }
}

/**
 * Set a page as 404 if the page identification failed
 */
export function* goTo404Page(): SagaIterator {
    yield fork(savePage, {
        ...dynamicRoutes.error404,
    });
}
